/* eslint-disable @eslint-react/hooks-extra/no-direct-set-state-in-use-effect */
import classNames from 'classnames';
import { shuffle } from 'lodash';
import PropTypes from 'prop-types';
import { useState, useCallback, useEffect, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import * as AppPropTypes from '../../lib/PropTypes';

import { useThemeClassName } from '../../contexts/ThemeContext';
import Button from '../buttons/Button';
import AudioPlayer from '../partials/AudioPlayer';
import LiveQuestionMessage from '../partials/LiveQuestionMessage';

import styles from '../../../styles/questions/choices.module.scss';

const propTypes = {
    layout: PropTypes.string,
    theme: PropTypes.oneOf(['trivia']),
    question: AppPropTypes.question,
    answer: AppPropTypes.answer,
    onAnswer: PropTypes.func,
    withoutBody: PropTypes.bool,
    isShuffled: PropTypes.bool,
    isLive: PropTypes.bool,
    isPreview: PropTypes.bool,
    isFirstAnswer: PropTypes.bool,
    isEnded: PropTypes.bool,
    largeImages: PropTypes.bool,
    autoPlay: PropTypes.bool,
    canPlay: PropTypes.bool,
    disabled: PropTypes.bool,
    className: PropTypes.string,
    innerClassName: PropTypes.string,
    titleClassName: PropTypes.string,
    audioClassName: PropTypes.string,
    buttonClassName: PropTypes.string,
};

const defaultProps = {
    layout: 'row',
    theme: null,
    question: null,
    answer: null,
    onAnswer: null,
    withoutBody: false,
    isShuffled: true,
    isLive: false,
    isPreview: false,
    isFirstAnswer: false,
    isEnded: false,
    autoPlay: false,
    largeImages: false,
    canPlay: false,
    disabled: false,
    className: null,
    innerClassName: null,
    titleClassName: null,
    audioClassName: null,
    buttonClassName: null,
};

function ChoicesQuestion({
    layout,
    theme: questionTheme,
    question,
    answer,
    onAnswer,
    withoutBody,
    isShuffled,
    isLive,
    isPreview,
    isFirstAnswer,
    isEnded: questionIsEnded,
    autoPlay,
    largeImages,
    canPlay,
    // endMessage,
    disabled,
    className,
    innerClassName,
    titleClassName,
    audioClassName,
    buttonClassName,
}) {
    const themeClassName = useThemeClassName(styles);
    const {
        id: questionId,
        audio = null,
        image: questionImage = null,
        choices = [],
        body = null,
        validAnswer = null,
        canAnswer = false,
        expired = false,
        explanation = null,
        type = null,
    } = question || {};

    const { url: questionImageUrl = null, alt: questionImageAlt = null } = questionImage || {};

    const { value = null, validated = false, valid = null } = answer || {};
    const choicesKey = `${questionId}-${
        choices ||
        []
            .map(({ id: choiceId }) => choiceId)
            .sort()
            .join('-')
    }`;

    const finalChoices = useMemo(() => {
        if (isShuffled) {
            const shuffledChoices = shuffle(choices || []);
            const allOfTheAboveChoice =
                shuffledChoices.find((choice) => choice.id === 'all') || null;

            if (allOfTheAboveChoice !== null) {
                const otherChoices = shuffledChoices.filter((choice) => choice.id !== 'all');
                return [...otherChoices, allOfTheAboveChoice];
            }
            return shuffledChoices;
        }
        return choices || [];
    }, [choicesKey]);

    const [selectedId, setSelectedId] = useState(value);
    useEffect(() => {
        setSelectedId(value);
    }, [value, setSelectedId]);

    const { length = 0 } = choices || [];
    const cols = useMemo(() => Math.ceil(Math.sqrt(length)), [length]);
    const colsPercent = useMemo(() => `${100 / cols}%`, [cols]);

    const onChoiceClick = useCallback(
        (id) => {
            if (!canAnswer && !isPreview) {
                return;
            }
            if (selectedId === null || !isFirstAnswer) {
                setSelectedId(id);
                if (onAnswer !== null) {
                    onAnswer(id);
                }
            }
        },
        [onAnswer, isFirstAnswer, selectedId, canAnswer],
    );

    const selectedChoice = finalChoices.reduce(
        (sel, choice) => (choice?.id === selectedId ? choice : sel),
        null,
    );
    const hasSelected = selectedChoice !== null;
    const isAnswered = answer !== null;
    const isAnswering = answer == null && canAnswer && !questionIsEnded;
    const isEnded = (!canAnswer && expired) || questionIsEnded;
    const isWaiting = !canAnswer && !isEnded;

    const hasValidAnswer =
        hasSelected &&
        (validAnswer === selectedChoice?.label || validAnswer === selectedChoice?.id);

    const isValid = isEnded && ((validated && valid === true) || hasValidAnswer);
    const isInvalid = isEnded && !isValid && hasSelected;
    const isValidOrInvalid = isValid || isInvalid;

    const hasExplanation = explanation !== null && isLive && isEnded;

    let liveMessage = (
        <LiveQuestionMessage
            isWaiting={isWaiting}
            isAnswering={isAnswering}
            isAnswered={isAnswered}
            isValid={isValid}
            isInvalid={isInvalid}
            isEnded={isEnded}
            isPreview={isPreview}
            body={body}
        />
    );

    return (
        <div
            className={classNames([
                styles.container,
                themeClassName,
                {
                    [className]: className !== null,
                    [styles[type]]: type !== null,
                    [styles.hasSelected]: hasSelected || isAnswered,
                    [styles.isValid]: isValid && !isPreview,
                    [styles.isInvalid]: isInvalid && !isPreview,
                    [styles.isComplete]: isEnded && !isValidOrInvalid,
                    [styles.isWaiting]: isWaiting,
                    [styles.isPreview]: isPreview,
                    [styles.canAnswer]: canAnswer,
                    [styles.disabled]: disabled,
                    [styles[`${layout}Layout`]]: layout !== null,
                    [styles[`${questionTheme}QuestionTheme`]]:
                        typeof styles[`${questionTheme}QuestionTheme`] !== 'undefined',
                },
            ])}
        >
            <div
                className={classNames([
                    styles.inner,
                    {
                        [innerClassName]: innerClassName !== null,
                    },
                ])}
            >
                {audio !== null && canAnswer ? (
                    <AudioPlayer
                        className={classNames([
                            styles.audio,
                            {
                                [audioClassName]: audioClassName !== null,
                            },
                        ])}
                        audio={audio}
                        autoPlay={autoPlay}
                        disabled={!canPlay}
                    />
                ) : null}
                <div className={styles.content}>
                    {body !== null && !withoutBody ? (
                        <h1
                            className={classNames([
                                styles.body,
                                'question-body',
                                {
                                    [titleClassName]: titleClassName !== null,
                                },
                            ])}
                        >
                            {!isLive && body !== null ? body : null}
                            {isLive && liveMessage !== null ? liveMessage : null}
                        </h1>
                    ) : null}
                    {questionImageUrl !== null && (canAnswer || isEnded) ? (
                        <div
                            className={classNames([
                                styles.questionImageContainer,
                                { [styles.large]: largeImages },
                            ])}
                        >
                            <img
                                className={styles.questionImage}
                                src={questionImageUrl}
                                alt={questionImageAlt || 'Question'}
                            />
                        </div>
                    ) : null}
                </div>
                <ul className={classNames([styles.choices, 'question-choices'])}>
                    {finalChoices.map(
                        ({
                            id,
                            pointsMultiplier = null,
                            showImageLabels = false,
                            label,
                            image = null,
                        }) => {
                            const { url: imageUrl = null, alt: imageAlt = null } = image || {};
                            const isValidAnswer = validAnswer === id || validAnswer === label;
                            const isSelected = selectedId === id;
                            const isImportant = isSelected || isValidAnswer;
                            return (
                                <li
                                    key={`choice-${id}`}
                                    className={classNames([
                                        styles.choice,
                                        {
                                            [styles.withImageLabels]: showImageLabels,
                                            [styles.withImage]: image !== null,
                                            [styles.liveWithImage]: isLive && image !== null,
                                            [styles.selected]: isSelected,
                                            [styles.validated]: validated,
                                            [styles.validAnswer]: isValidAnswer,
                                            [styles.hidden]: hasExplanation && !isImportant,
                                        },
                                    ])}
                                    style={
                                        layout === 'grid'
                                            ? { width: `calc(${colsPercent} - 10px)` }
                                            : null
                                    }
                                >
                                    <Button
                                        className={classNames([
                                            styles.button,
                                            {
                                                [buttonClassName]: buttonClassName !== null,
                                            },
                                        ])}
                                        disabled={disabled}
                                        onClick={() => {
                                            onChoiceClick(id);
                                        }}
                                    >
                                        {imageUrl !== null ? (
                                            <img
                                                className={classNames([
                                                    styles.image,
                                                    { [styles.large]: largeImages },
                                                ])}
                                                src={imageUrl}
                                                alt={imageAlt || label}
                                            />
                                        ) : null}
                                        {imageUrl === null || showImageLabels ? (
                                            <span className={styles.label}>{label}</span>
                                        ) : null}
                                        {pointsMultiplier !== null ? (
                                            <span className={styles.pointsMultiplier}>
                                                <span className={styles.amount}>
                                                    {pointsMultiplier}
                                                </span>
                                                <span className={styles.points}>x pts</span>
                                            </span>
                                        ) : null}
                                    </Button>
                                </li>
                            );
                        },
                    )}
                </ul>
                {hasExplanation ? (
                    <div className={styles.explanation}>
                        <p className={styles.explanationText}>{explanation}</p>
                    </div>
                ) : null}
                {isLive && isWaiting ? (
                    <p
                        className={classNames([
                            styles.wait,
                            {
                                [titleClassName]: titleClassName !== null,
                            },
                        ])}
                    >
                        <FormattedMessage
                            defaultMessage="Waiting for next question..."
                            description="Question message"
                        />
                    </p>
                ) : null}
            </div>
        </div>
    );
}

ChoicesQuestion.propTypes = propTypes;
ChoicesQuestion.defaultProps = defaultProps;

export default ChoicesQuestion;
